import { useEffect, useRef, useState } from "react";
import { Send, Loader2, Trash2, MessageCircleQuestion } from "lucide-react";
import { toast } from "sonner";
import { api, BACKEND, errMsg } from "@/lib/api";
import { Markdown } from "@/components/course/Markdown";

const SUGGESTIONS = ["Explique-moi la notion principale simplement", "Quelles sont les dates importantes ?", "Fais-moi un plan pour réviser ce cours"];

export const AskTab = ({ c }) => {
  const [msgs, setMsgs] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { api.get(`/courses/${c.course_id}/chat`).then(({ data }) => setMsgs((prev) => [...data, ...prev])).catch(() => {}); }, [c.course_id]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [msgs]);

  const ask = async (question) => {
    if (!question.trim() || busy) return;
    setQ("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: question }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${BACKEND}/api/courses/${c.course_id}/ask`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ question }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Erreur");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const l of lines) {
          const payload = l.replace(/^data: /, "");
          if (payload === "[DONE]") continue;
          let ev;
          try { ev = JSON.parse(payload); } catch { continue; }
          if (ev.error) throw new Error(ev.error);
          setMsgs((m) => {
            const n = [...m];
            const last = n[n.length - 1];
            if (last?.role === "assistant") n[n.length - 1] = { ...last, content: last.content + ev.delta };
            else n.push({ role: "assistant", content: ev.delta });
            return n;
          });
        }
      }
    } catch (e) {
      toast.error(e.message || errMsg(e));
      setMsgs((m) => (m[m.length - 1]?.role === "assistant" && !m[m.length - 1].content ? m.slice(0, -2) : m));
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => { await api.delete(`/courses/${c.course_id}/chat`).catch(() => {}); setMsgs([]); };

  return (
    <div className="card-flat flex flex-col p-5 sm:p-6" data-testid="ask-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">L'IA répond <b className="text-foreground">uniquement à partir de ton cours</b>.</p>
        {msgs.length > 0 && <button data-testid="clear-chat-button" onClick={clear} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /> Effacer</button>}
      </div>
      <div className="mt-5 min-h-[260px] flex-1 space-y-4 overflow-y-auto" style={{ maxHeight: 520 }}>
        {msgs.length === 0 && (
          <div className="flex flex-col items-center py-10 text-center">
            <MessageCircleQuestion className="h-9 w-9 text-primary" />
            <p className="mt-3 font-semibold">Pose une question sur ce cours</p>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              {SUGGESTIONS.map((s, i) => <button key={s} data-testid={`ask-suggestion-${i}`} onClick={() => ask(s)} className="rounded-full border px-3.5 py-1.5 text-xs font-medium hover:border-primary/40">{s}</button>)}
            </div>
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : ""}`} data-testid={`chat-message-${m.role}-${i}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm ${m.role === "user" ? "bg-brand text-white" : "bg-muted/60"}`}>
              {m.role === "user" ? m.content : m.content ? <Markdown className="!text-sm">{m.content}</Markdown> : <Loader2 className="h-4 w-4 animate-spin text-primary" />}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); ask(q); }} className="mt-4 flex gap-2">
        <input data-testid="ask-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ex. : Quelle est la différence entre… ?" maxLength={2000}
          className="h-12 flex-1 rounded-full border bg-background px-5 text-sm outline-none focus:border-primary/50 focus:ring-4 focus:ring-primary/10" />
        <button data-testid="ask-submit-button" disabled={busy || !q.trim()} className="bg-brand inline-flex h-12 w-12 items-center justify-center rounded-full text-white disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </form>
    </div>
  );
};
